import { useState, useEffect } from 'react';
import { Calculator, Save, RotateCcw, Plus, Trash2, Shield, Settings, Eye, Percent, FileText } from 'lucide-react';

const TaxSettings = ({ settings, onSave, onChange }) => {
  const [formData, setFormData] = useState({
    taxExempt: settings?.taxExempt || false,
    taxPercentage: settings?.taxPercentage || 20,
    taxName: settings?.taxName || 'VAT',
    taxNumber: settings?.taxNumber || '',
    // Multiple tax rates
    enableMultipleTaxRates: settings?.enableMultipleTaxRates || false,
    taxRates: settings?.taxRates || [
      { name: 'Standard Rate', rate: 20, isDefault: true },
      { name: 'Reduced Rate', rate: 5, isDefault: false },
      { name: 'Zero Rate', rate: 0, isDefault: false }
    ],
    // Tax calculation
    taxCalculationMethod: settings?.taxCalculationMethod || 'exclusive',
    roundTaxToNearestPenny: settings?.roundTaxToNearestPenny !== false,
    showTaxBreakdown: settings?.showTaxBreakdown || true
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (onChange) {
      onChange();
    }
  }, [formData, onChange]);

  const handleSubmit = () => {
    onSave({ ...settings, ...formData });
  };

  const addTaxRate = () => {
    const newRate = {
      name: `Tax Rate ${formData.taxRates.length + 1}`,
      rate: 0,
      isDefault: false
    };
    setFormData({
      ...formData,
      taxRates: [...formData.taxRates, newRate]
    });
  };

  const updateTaxRate = (index, field, value) => {
    const newRates = [...formData.taxRates];
    newRates[index][field] = value;
    
    // If setting as default, unset others
    if (field === 'isDefault' && value) {
      newRates.forEach((rate, i) => {
        if (i !== index) rate.isDefault = false;
      });
    }
    
    setFormData({ ...formData, taxRates: newRates });
  };

  const removeTaxRate = (index) => {
    const newRates = formData.taxRates.filter((_, i) => i !== index);
    setFormData({ ...formData, taxRates: newRates });
  };

  const calculateTaxExample = (amount, rate, method) => {
    if (method === 'inclusive') {
      const taxAmount = (amount * rate) / (100 + rate);
      const netAmount = amount - taxAmount;
      return { netAmount, taxAmount, totalAmount: amount };
    } else {
      const taxAmount = (amount * rate) / 100;
      const totalAmount = amount + taxAmount;
      return { netAmount: amount, taxAmount, totalAmount };
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Setup', icon: Shield },
    { id: 'calculation', label: 'Calculation', icon: Calculator },
    { id: 'rates', label: 'Tax Rates', icon: Percent },
    { id: 'display', label: 'Display Options', icon: FileText }
  ];

  const getCountryPresets = () => [
    {
      name: 'United Kingdom',
      taxName: 'VAT',
      rates: [
        { name: 'Standard Rate', rate: 20, isDefault: true },
        { name: 'Reduced Rate', rate: 5, isDefault: false },
        { name: 'Zero Rate', rate: 0, isDefault: false }
      ]
    },
    {
      name: 'United States',
      taxName: 'Sales Tax',
      rates: [
        { name: 'Standard Rate', rate: 8.25, isDefault: true },
        { name: 'No Tax', rate: 0, isDefault: false }
      ]
    },
    {
      name: 'Canada',
      taxName: 'GST/HST',
      rates: [
        { name: 'GST', rate: 5, isDefault: true },
        { name: 'HST', rate: 13, isDefault: false },
        { name: 'No Tax', rate: 0, isDefault: false }
      ]
    }
  ];

  const applyCountryPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      taxName: preset.taxName,
      taxRates: preset.rates,
      taxPercentage: preset.rates.find(r => r.isDefault)?.rate || preset.rates[0].rate,
      enableMultipleTaxRates: preset.rates.length > 1
    }));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Tax Registration Status</h3>
              <p className="text-blue-700 text-sm mb-6">Configure your business tax status and registration details</p>
              
              {/* Tax Exempt Toggle */}
              <div className="bg-white border border-blue-200 rounded-lg p-5 mb-6">
                <div className="flex items-start">
                  <input
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                    type="checkbox"
                    id="taxExempt"
                    checked={formData.taxExempt}
                    onChange={(e) => setFormData({...formData, taxExempt: e.target.checked})}
                  />
                  <div className="ml-4">
                    <label className="text-base font-semibold text-gray-900" htmlFor="taxExempt">
                      Tax Exempt Business
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      Check this if your business is not required to charge tax on sales. This will disable all tax calculations.
                    </p>
                  </div>
                </div>
              </div>

              {!formData.taxExempt && (
                <>
                  {/* Country Presets */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Quick Setup by Country</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {getCountryPresets().map((preset) => (
                        <button
                          key={preset.name}
                          type="button"
                          onClick={() => applyCountryPreset(preset)}
                          className="p-4 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        >
                          <div className="font-medium text-gray-800">{preset.name}</div>
                          <div className="text-sm text-gray-600">{preset.taxName}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {preset.rates.length} rate{preset.rates.length !== 1 ? 's' : ''}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Manual Configuration */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h4 className="font-semibold text-gray-800 mb-4">Tax Registration Details</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tax Name</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          value={formData.taxName}
                          onChange={(e) => setFormData({...formData, taxName: e.target.value})}
                          placeholder="VAT, Sales Tax, GST, etc."
                        />
                        <p className="text-xs text-gray-500 mt-1">What your tax is called (e.g., VAT, Sales Tax, GST)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tax Registration Number</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                          value={formData.taxNumber}
                          onChange={(e) => setFormData({...formData, taxNumber: e.target.value.toUpperCase()})}
                          placeholder="GB123456789"
                        />
                        <p className="text-xs text-gray-500 mt-1">Your official tax registration number</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 'calculation':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Tax Calculation Method</h3>
              <p className="text-green-700 text-sm mb-6">Configure how taxes are calculated and displayed</p>
              
              <div className="space-y-6">
                {/* Basic Rate */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Default Tax Rate</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
                      <div className="relative">
                        <input
                          type="number"
                          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-xl text-center"
                          value={formData.taxPercentage}
                          onChange={(e) => setFormData({...formData, taxPercentage: parseFloat(e.target.value) || 0})}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 text-xl">%</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Calculation Method</label>
                      <div className="space-y-3">
                        <label className="flex items-start">
                          <input
                            type="radio"
                            name="calculationMethod"
                            value="exclusive"
                            checked={formData.taxCalculationMethod === 'exclusive'}
                            onChange={(e) => setFormData({...formData, taxCalculationMethod: e.target.value})}
                            className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500 focus:ring-2 mt-0.5"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">Tax Exclusive</div>
                            <div className="text-xs text-gray-600">Tax is added to the item price</div>
                          </div>
                        </label>
                        
                        <label className="flex items-start">
                          <input
                            type="radio"
                            name="calculationMethod"
                            value="inclusive"
                            checked={formData.taxCalculationMethod === 'inclusive'}
                            onChange={(e) => setFormData({...formData, taxCalculationMethod: e.target.value})}
                            className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500 focus:ring-2 mt-0.5"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">Tax Inclusive</div>
                            <div className="text-xs text-gray-600">Tax is included in the item price</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rounding Options */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Rounding & Precision</h4>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input
                        className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mt-0.5"
                        type="checkbox"
                        id="roundTaxToNearestPenny"
                        checked={formData.roundTaxToNearestPenny}
                        onChange={(e) => setFormData({...formData, roundTaxToNearestPenny: e.target.checked})}
                      />
                      <div className="ml-3">
                        <label className="text-sm font-medium text-gray-900" htmlFor="roundTaxToNearestPenny">
                          Round tax to nearest penny
                        </label>
                        <p className="text-sm text-gray-600">Prevents fractional penny amounts in tax calculations</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Calculation Example */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-3">Live Calculation Example</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div><span className="font-medium">Method:</span> {formData.taxCalculationMethod === 'exclusive' ? 'Tax Exclusive' : 'Tax Inclusive'}</div>
                        <div><span className="font-medium">Rate:</span> {formData.taxPercentage}% {formData.taxName}</div>
                        <div><span className="font-medium">Example Amount:</span> £100.00</div>
                      </div>
                    </div>
                    <div>
                      {(() => {
                        const calc = calculateTaxExample(100, formData.taxPercentage, formData.taxCalculationMethod);
                        return (
                          <div className="text-sm bg-white border border-gray-200 rounded p-3">
                            {formData.taxCalculationMethod === 'exclusive' ? (
                              <div className="space-y-1">
                                <div className="flex justify-between">
                                  <span>Net Amount:</span>
                                  <span>£{calc.netAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>{formData.taxName}:</span>
                                  <span>£{calc.taxAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between font-semibold border-t pt-1">
                                  <span>Total:</span>
                                  <span>£{calc.totalAmount.toFixed(2)}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex justify-between font-semibold">
                                  <span>Total Amount:</span>
                                  <span>£{calc.totalAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                  <span>Includes {formData.taxName}:</span>
                                  <span>£{calc.taxAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                  <span>Net Amount:</span>
                                  <span>£{calc.netAmount.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'rates':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Multiple Tax Rates</h3>
              <p className="text-purple-700 text-sm mb-6">Set up different tax rates for different types of products or services</p>
              
              <div className="space-y-6">
                {/* Enable Multiple Rates */}
                <div className="bg-white border border-purple-200 rounded-lg p-5">
                  <div className="flex items-start">
                    <input
                      className="w-5 h-5 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 mt-1"
                      type="checkbox"
                      id="enableMultipleTaxRates"
                      checked={formData.enableMultipleTaxRates}
                      onChange={(e) => setFormData({...formData, enableMultipleTaxRates: e.target.checked})}
                    />
                    <div className="ml-4">
                      <label className="text-base font-semibold text-gray-900" htmlFor="enableMultipleTaxRates">
                        Enable multiple tax rates
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        Allow different tax rates for different products (e.g., standard rate, reduced rate, zero rate)
                      </p>
                    </div>
                  </div>
                </div>

                {formData.enableMultipleTaxRates && (
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold text-gray-800">Tax Rate Configuration</h4>
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                        onClick={addTaxRate}
                      >
                        <Plus size={16} className="mr-2" />
                        Add Tax Rate
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {formData.taxRates.map((rate, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                            <div className="lg:col-span-5">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Rate Name</label>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                value={rate.name}
                                onChange={(e) => updateTaxRate(index, 'name', e.target.value)}
                                placeholder="e.g., Standard Rate, Reduced Rate"
                              />
                            </div>
                            
                            <div className="lg:col-span-3">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Rate (%)</label>
                              <div className="relative">
                                <input
                                  type="number"
                                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                  value={rate.rate}
                                  onChange={(e) => updateTaxRate(index, 'rate', parseFloat(e.target.value) || 0)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                />
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">%</span>
                              </div>
                            </div>
                            
                            <div className="lg:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Default</label>
                              <div className="flex items-center justify-center h-10">
                                <input
                                  className="w-5 h-5 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 focus:ring-2"
                                  type="radio"
                                  name="defaultTaxRate"
                                  checked={rate.isDefault}
                                  onChange={(e) => updateTaxRate(index, 'isDefault', e.target.checked)}
                                />
                              </div>
                            </div>
                            
                            <div className="lg:col-span-2 flex justify-center">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center w-10 h-10 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => removeTaxRate(index)}
                                disabled={formData.taxRates.length <= 1}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                          
                          {rate.isDefault && (
                            <div className="mt-2 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                              This is the default rate used for new line items
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'display':
        return (
          <div className="space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">Display Options</h3>
              <p className="text-orange-700 text-sm mb-6">Configure how tax information appears on your documents</p>
              
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-semibold text-gray-800 mb-4">Tax Information Display</h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <input
                      className="w-5 h-5 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2 mt-0.5"
                      type="checkbox"
                      id="showTaxBreakdown"
                      checked={formData.showTaxBreakdown}
                      onChange={(e) => setFormData({...formData, showTaxBreakdown: e.target.checked})}
                    />
                    <div className="ml-3">
                      <label className="text-sm font-medium text-gray-900" htmlFor="showTaxBreakdown">
                        Show detailed tax breakdown
                      </label>
                      <p className="text-sm text-gray-600">Display tax calculations separately on invoices and quotes</p>
                    </div>
                  </div>
                </div>

                {formData.showTaxBreakdown && (
                  <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-3">Preview: How tax appears on documents</h5>
                    <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span>£100.00</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>{formData.taxName} ({formData.taxPercentage}%):</span>
                          <span>£{((100 * formData.taxPercentage) / 100).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Total:</span>
                          <span>£{(100 + (100 * formData.taxPercentage) / 100).toFixed(2)}</span>
                        </div>
                      </div>
                      {formData.taxNumber && (
                        <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                          {formData.taxName} Reg: {formData.taxNumber}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getTaxStatus = () => {
    if (formData.taxExempt) {
      return { status: 'Tax Exempt', color: 'gray', description: 'No tax calculations' };
    } else if (formData.enableMultipleTaxRates) {
      return { status: 'Multiple Rates', color: 'purple', description: `${formData.taxRates.length} tax rates configured` };
    } else {
      return { status: 'Single Rate', color: 'blue', description: `${formData.taxPercentage}% ${formData.taxName}` };
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="flex items-center text-xl font-semibold text-gray-800">
              <Calculator size={24} className="mr-3" />
              Tax Settings
            </h5>
            <p className="text-sm text-gray-600 mt-1">
              Configure VAT, sales tax, and other tax calculations for your invoices
            </p>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={16} className="mr-2" />
            {showPreview ? 'Hide Overview' : 'Show Overview'}
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Preview Section */}
        {showPreview && !formData.taxExempt && (
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h6 className="text-lg font-semibold text-gray-800 mb-4">Tax Configuration Overview</h6>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Current Configuration</h4>
                  <div className="space-y-3">
                    {(() => {
                      const status = getTaxStatus();
                      return (
                        <div className={`p-3 rounded-lg border ${
                          status.color === 'gray' ? 'bg-gray-50 border-gray-200 text-gray-700' :
                          status.color === 'purple' ? 'bg-purple-50 border-purple-200 text-purple-700' :
                          'bg-blue-50 border-blue-200 text-blue-700'
                        }`}>
                          <div className="font-medium">{status.status}</div>
                          <div className="text-sm">{status.description}</div>
                        </div>
                      );
                    })()}
                    
                    <div className="text-sm text-gray-600">
                      <div><span className="font-medium">Tax Name:</span> {formData.taxName}</div>
                      {formData.taxNumber && <div><span className="font-medium">Registration:</span> {formData.taxNumber}</div>}
                      <div><span className="font-medium">Method:</span> {formData.taxCalculationMethod === 'exclusive' ? 'Tax Exclusive' : 'Tax Inclusive'}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Active Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.enableMultipleTaxRates && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Multiple Rates
                      </span>
                    )}
                    {formData.roundTaxToNearestPenny && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Penny Rounding
                      </span>
                    )}
                    {formData.showTaxBreakdown && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Tax Breakdown
                      </span>
                    )}
                    {formData.taxCalculationMethod === 'inclusive' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Tax Inclusive
                      </span>
                    )}
                    {(!formData.enableMultipleTaxRates && !formData.roundTaxToNearestPenny && !formData.showTaxBreakdown && formData.taxCalculationMethod === 'exclusive') && (
                      <span className="text-sm text-gray-500 italic">Standard configuration</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tax Exempt Notice */}
        {formData.taxExempt && showPreview && (
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 text-center">
              <Shield size={48} className="mx-auto text-gray-400 mb-3" />
              <h4 className="font-semibold text-gray-800 mb-2">Tax Exempt Business</h4>
              <p className="text-sm text-gray-600">
                Tax calculations are disabled. No tax will be added to invoices or quotes.
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-8 border-t border-gray-200 mt-8">
          <button 
            type="button" 
            className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RotateCcw size={16} className="mr-2" />
            Reset to Defaults
          </button>
          <button 
            type="button"
            onClick={handleSubmit}
            className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={16} className="mr-2" />
            Save Tax Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaxSettings;